import { gql } from "@apollo/client";
import { IPerformanceChartData, IDashboardSavedView, ISavedView } from "./types";

export const FETCH_USER_PREVIEWS = gql`
  query fetchUsersAndPreviews($user_ids: [String!]!) {
    fetchUsersAndPreviews(user_ids: $user_ids) {
      user_id
      user {
        id
        first_name
        last_name
        lead_source_filter
        industry_filter
        lead_source_white_list_array
        industry_white_list_array
        leads_in_queue
      }
      user_preview {
        AssignedLead
        OwnedLead
        TotalLead
        CCDials
        GeneralCallBackDials
        FollowUpDials
        ScheduledCallBacks
        Demos
        DecisionCalls
        AllScheduledEvents
        UpcomingDials
      }
    }
  }
`;

export const FETCH_USER_CALL_REPORT_PIPELINE_COLUMNS = gql`
  query fetchUserCallReportEventColumns {
    fetchUser {
      id
      visible_call_report_pipeline_columns_computed
    }
  }
`;

export const FETCH_PERFORMANCE_CHART = gql`
  query fetchPerformanceChartV2($dashboard_args: DashBoardSavedViewArgument!, $dashboard_type: DashboardType!) {
    fetchPerformanceChartV2(dashboard_args: $dashboard_args, dashboard_type: $dashboard_type) {
      id
      computed_id
      row_label
      metric
      metric_label
      metric_type
      metric_tooltip
      value
      position
      rank
      lowerbound_date
      upperbound_date
      team_id
      user_id
      site_id
      profile_image
      organization_id
      role
      __typename
    }
  }
`;

export const FETCH_DASHBOARD_DEFAULT_VIEW = gql`
  query fetchDefaultSavedView {
    fetchDefaultSavedView {
      id
      name
      is_default_view
      lead_filter {
        Channels
        LeadSources
        LeadCreationSources
        primary_industries
        sub_industries
        NextScheduledEventDays
        NextScheduledEventTypes
      }
      rep_filter {
        team_ids
        site_ids
        roles
        user_ids
      }
      column_options {
        visible_reporting_fields
        visible_coaching_fields
        visible_activity_fields
        visible_event_fields
        visible_pipeline_fields
        visible_transfer_fields
        visible_transfer_quality_fields
      }
      group_option
      measure_by
      lowerbound_date
      upperbound_date
      date_range
    }
  }
`;

export const FETCH_ALL_SAVED_VIEW = gql`
  query fetchAllSavedViews {
    fetchAllSavedViews {
      id
      name
    }
  }
`;

export const FETCH_DASHBOARD_SAVED_VIEW = gql`
  query fetchDashboardSavedView($saved_view_id: String!) {
    fetchDashboardSavedView(saved_view_id: $saved_view_id) {
      id
      name
      is_default_view
      lead_activity_channel
      lead_filter {
        Channels
        LeadSources
        LeadCreationSources
        primary_industries
        sub_industries
        NextScheduledEventDays
        NextScheduledEventTypes
        sequences
        custom_fields
      }
      rep_filter {
        team_ids
        site_ids
        roles
        user_ids
      }
      column_options {
        visible_reporting_fields
        visible_coaching_fields
        visible_activity_fields
        visible_event_fields
        visible_pipeline_fields
        visible_transfer_fields
        visible_transfer_quality_fields
      }
      group_option
      measure_by
      lowerbound_date
      upperbound_date
      date_range
    }
  }
`;

export const UPDATE_DASHBOARD_SAVED_VIEW = gql`
  mutation updateDashboardSavedView($data: DashBoardSavedViewArgument!, $name: String!, $saved_view_id: String!) {
    updateDashboardSavedView(data: $data, name: $name, saved_view_id: $saved_view_id) {
      id
      name
    }
  }
`;

export const FETCH_DASHBOARD_SHARED_VIEW = gql`
  query fetchDashboardSharedView($saved_view_id: String!, $dashboard_type: DashboardType!) {
    fetchDashboardSharedView(saved_view_id: $saved_view_id, dashboard_type: $dashboard_type) {
      saved_view_data {
        id
        name
        date_range
        measure_by
        upperbound_date
        lowerbound_date
      }
      group_option
      stat_items {
        id
        computed_id
        row_label
        metric
        metric_label
        profile_image
        metric_type
        metric_tooltip
        value
        position
        rank
        lowerbound_date
        upperbound_date
        site_id
        team_id
        user_id
        organization_id
        __typename
      }
    }
  }
`;

export const EXPORT_DASHBOARD_VIEW = gql`
  mutation exportDashboardView(
    $dashboard_args: DashBoardSavedViewArgument!
    $dashboard_type: DashboardType!
    $group_references: [String]
  ) {
    exportDashboardView(
      dashboard_args: $dashboard_args
      dashboard_type: $dashboard_type
      group_references: $group_references
    ) {
      url
    }
  }
`;

export const FETCH_TOP_FUNNEL_LEAD_COUNT = gql`
  query fetchTopFunnelLeadCount($pipelineManagementArg: PipelineManagementArg!) {
    fetchTopFunnelLeadCount(pipeline_management_arg: $pipelineManagementArg)
  }
`;

export const FETCH_BOTTOM_FUNNEL_LEAD_COUNT = gql`
  query fetchBottomFunnelLeadCount($pipelineManagementArg: PipelineManagementArg!) {
    fetchBottomFunnelLeadCount(pipeline_management_arg: $pipelineManagementArg)
  }
`;

export const EXPORT_PIPELINE_REPORT = gql`
  mutation exportPipelineReport(
    $pipeline_export_arg: PipelineExportArg!
    $column_options: [String!]!
    $funnel: String
  ) {
    exportPipelineReport(pipeline_export_arg: $pipeline_export_arg, column_options: $column_options, funnel: $funnel) {
      url
    }
  }
`;

export const FETCH_PIPELINE_COLUMNS = gql`
  query fetchPipelineColumn {
    fetchPipelineColumn {
      metric_label
      metric
    }
  }
`;

export interface IfetchUsersAndPreviews {
  fetchUsersAndPreviews?: {
    user_id?: string;
    user?: {
      id?: string;
      first_name?: string;
      last_name?: string;
      lead_source_filter?: string;
      industry_filter?: string;
      lead_source_white_list_array?: string[];
      industry_white_list_array?: string[];
      leads_in_queue?: number;
    };
    user_preview?: {
      AssignedLead?: number;
      OwnedLead?: number;
      TotalLead?: number;
      CCDials?: number;
      GeneralCallBackDials?: number;
      FollowUpDials?: number;
      ScheduledCallBacks?: number;
      Demos?: number;
      DecisionCalls?: number;
      AllScheduledEvents?: number;
      UpcomingDials?: number;
    };
  }[];
}

export interface IfetchUserCallReportEventColumns {
  fetchUser: {
    id: string;
    visible_call_report_event_columns_computed: string[];
    visible_call_report_pipeline_columns_computed: string[];
  };
}

export interface IfetchPerformanceChartV2 {
  fetchPerformanceChartV2?: IPerformanceChartData[];
}

export interface IfetchDefaultSavedView {
  fetchDefaultSavedView: IDashboardSavedView;
}

export interface IfetchAllSavedViews {
  fetchAllSavedViews: ISavedView[];
}

export interface IfetchDashboardSavedView {
  fetchDashboardSavedView?: IDashboardSavedView;
}

export interface IExportDashboardView {
  exportDashboardView?: {
    url?: string;
  };
}
